import { InteractionWorkspaceProps } from "./types";
import { useEmailLoadingMethods, useScrollEmailConversationToBottom } from "@/hooks/emails";
import { useQuery } from "@tanstack/react-query";
import {
	fetchSessionHistoryMessages,
	HistoryMessagesKey,
} from "@/repository/queryFns";
import { EmailConversation, EmailConversationHeader } from "@/components/molecules/emailConversation";
import { useEffect, useRef, useState } from "react";
import { HttpStatusCode } from "axios";
import { useIntl } from "react-intl";
import { UnavailableConversationPlaceholder } from "./UnavailableConversationPlaceholder";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";
import { useMessage } from "@/common/messages";
import { SUBJECT_CHANGE_INFO_MESSAGE_KEY } from "@/common/constants";
import {
	AppCacheState,
	AppCacheActions,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { useShallow } from "zustand/react/shallow";
import { EmailThreadSortOrder } from "@/components/molecules/email";

export const EmailHistoryInteractionContent = (
	props: InteractionWorkspaceProps
) => {
	const { historyItem } = props;
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [isError, setIsError] = useState<boolean>();
	const { show: showMessage, hide: hideMessage } = useMessage();

	const intl = useIntl();

	const { cache, setCache } = useAppCacheStore(
		useShallow((state: AppCacheState & AppCacheActions) => ({
			cache: state.cache,
			setCache: state.setCache,
		}))
	);
	const sortOrder: EmailThreadSortOrder = cache[CacheKey.EmailThreadSortOrder] ?? "oldest-first";

	const { data } = useQuery({
		queryFn: async () => {
			try {
				addBreadcrumb(
					"ui_interaction",
					"Fetch email history messages",
					historyItem.interactionId
				);
				var messages = await fetchSessionHistoryMessages(
					historyItem.interactionId
				);
				setIsError(false);

				return messages;
			} catch (e: any) {
				captureError(e, "Error while fetching email history messages", {
					interactionId: historyItem.interactionId,
				});
				switch (e.response.status) {
					case HttpStatusCode.NotFound:
						setErrorMessage(
							intl.formatMessage({
								id: "History.InteractionContentWasMovedOrDeleted",
							})
						);
						break;
					case HttpStatusCode.BadRequest:
						setErrorMessage(
							intl.formatMessage({
								id: "History.InteractionContentNotAvailable",
							})
						);
						break;
					default:
						setErrorMessage(
							intl.formatMessage({ id: "History.ErrorWhenLoadingTheMessages" })
						);
						break;
				}
				setIsError(true);
			}

			return null;
		},
		queryKey: [HistoryMessagesKey, historyItem.interactionId],
		refetchOnMount: "always",
		gcTime: 0,
		throwOnError: true,
	});

	const emailMethods = useEmailLoadingMethods(
		historyItem.interactionId,
		true,
		data?.messages,
		true,
		undefined,
		undefined,
		false,
		undefined,
		sortOrder
	);

	const conversationScrollRef = useRef<HTMLDivElement>(null);
	useScrollEmailConversationToBottom(conversationScrollRef, sortOrder, emailMethods.emails.length);

	useEffect(() => {
		const isSubjectChangedByLastMessage = data?.isSubjectChangedByLastMessage;

		if (isSubjectChangedByLastMessage) {
			showMessage("historyWorkspace", {
				key: SUBJECT_CHANGE_INFO_MESSAGE_KEY,
				text: intl.formatMessage({
					id: "Interaction.EmailEditor.SubjectChangedByLastMessage",
				}),
				intent: "info",
				isPermanent: true,
				bodyStyle: {
					textWrap: "wrap",
					paddingTop: "8px",
					paddingBottom: "8px",
				},
			});
		} else {
			hideMessage("historyWorkspace", SUBJECT_CHANGE_INFO_MESSAGE_KEY);
		}
	}, [data]);

	return (
		<>
			{!isError && data && emailMethods.emails.length > 0 && (
				<EmailConversation
					scrollRef={conversationScrollRef}
					header={
						<EmailConversationHeader
							messages={data?.messages}
							sessionId={historyItem.interactionId}
							isHistory={true}
							isPreview={false}
							sortOrder={sortOrder}
							onSortChange={(order) => setCache(CacheKey.EmailThreadSortOrder, order)}
						/>
					}
					emails={emailMethods.emails}
				/>
			)}
			{!isError && data && emailMethods.emails.length === 0 && (
				<UnavailableConversationPlaceholder
					message={intl.formatMessage({
						id: "History.InteractionContentWasMovedOrDeleted",
					})}
				/>
			)}
			{isError && <UnavailableConversationPlaceholder message={errorMessage} />}
		</>
	);
};
